function joinFrSc(casenumber) {
    let result = casenumber;
    // Use case-insensitive global replace for robustness
    if (result.includes("f r")) result = result.replace(/f\s+r/gi, "fr");
    if (result.includes("s c")) result = result.replace(/s\s+c/gi, "sc");
    if (result.includes("c h c")) result = result.replace(/c\s+h\s+c/gi, "chc");
    if (result.includes(" c a ")) result = result.replace(/\s+c\s+a\s+/gi, " ca "); // Keep spaces around ca if intended
    if (result.includes("l a")) result = result.replace(/l\s+a/gi, "la");
    return result;
}

function extractCaseNumSlashYearLast(casenumber) {

    const patterns = [
        /\b(\d{1,5}[a-z]{0,2}\s*[_\-/]\s*(?:(?:19|20)\d{2}|\d{2}))\b/gi,  
        /\b(\d{1,5}[a-z]{0,2})\s+((?:19|20)\d{2}|\d{2})\b/gi,             
        /\b(\d{1,5}[a-z]{0,2}\b.*?of.*?\b((?:19|20)\d{2}|\d{2}))\b/gi,     
        /\b(\d{1,5}[a-z]{0,2}\b.*?\/.*?\b((?:19|20)\d{2}|\d{2}))\b/gi,    
    ];

    let allFound = [];
    for (const pattern of patterns) {
        const found = casenumber.match(pattern);
        if (found) {
            allFound.push(...found);
        }
    }

    const uniqueFound = [...new Set(allFound)]; // Get unique matches

    const filteredFound = uniqueFound.filter(match => {
        // Requires at least 2 digits before the year/separator OR contains letters
        return /\d{2,}|[a-z]/i.test(match.split(/[_\-/ ]/)[0]);
    });


    if (filteredFound.length > 0) {
        return filteredFound; // Return filtered list of potential court case num/year strings
    } else if (uniqueFound.length > 0) {
         // console.log("Extraction filter removed all matches, returning unique unfiltered:", uniqueFound);
         return uniqueFound;
    }
     else {
        // console.log("NA\nNo num/year pattern found in:", casenumber, "\n");
        return "NA";
    }
}

// --- Main Standardization Function ---

export const standardizeCaseNumber = (caseNumberInput, court = 'ca') => {
    if (!caseNumberInput || typeof caseNumberInput !== 'string' || caseNumberInput.trim() === '') {
        return caseNumberInput;
    }

    // 1. Initial Cleaning & Lowercase
    let processedInput = caseNumberInput.trim().toLowerCase();
    processedInput = processedInput.replace(/\s+/g, ' ').trim();

    // --- SLR/NLR Check ---
    // Pattern: YYYY vX slr/nlr page
    const slrNlrWithVolumeRegex = /\b((?:19|20)\d{2})\s+(v\d+)\s+(slr|nlr)\s+(\d+)\b/i;
    // Pattern: YYYY slr/nlr page
    const slrNlrWithoutVolumeRegex = /\b((?:19|20)\d{2})\s+(slr|nlr)\s+(\d+)\b/i;

    const matchWithVolume = processedInput.match(slrNlrWithVolumeRegex);
    const matchWithoutVolume = processedInput.match(slrNlrWithoutVolumeRegex);

    if (matchWithVolume) {
        // Format: YYYY/vX-report-page
        const [, year, volume, report, page] = matchWithVolume;
        const standardizedReport = report.toLowerCase() === 'slr' ? 'sri-lr' : 'nlr';

        return `${year}/${volume}-${standardizedReport}-${page}`;
    } else if (matchWithoutVolume) {
        // Format: YYYY-report-page
        const [, year, report, page] = matchWithoutVolume;
        const standardizedReport = report.toLowerCase() === 'slr' ? 'sri-lr' : 'nlr';
        return `${year}-${standardizedReport}-${page}`; 
    }

    // Allow essential chars: letters, numbers, space, hyphen, slash
    processedInput = processedInput.replace(/[^\w\s/-]/g, ' ');
    processedInput = processedInput.replace(/\s+/g, ' ').trim(); 
    processedInput = joinFrSc(processedInput); 

    // 2. Extract Potential Number/Year Parts
    const extractedMatches = extractCaseNumSlashYearLast(processedInput);

    // 3. Handle Extraction Failure
    if (!extractedMatches || extractedMatches === "NA" || extractedMatches.length === 0) {
        // console.warn("Std: Could not extract num/year pattern. Hyphenating cleaned input:", processedInput);
        return processedInput.replace(/\s+/g, '-');
    }

    // 4. Select and Parse the *Last* Sensible Match
    let cnum = '';
    let year = '';
    let successfulParse = false;
    let parsedMatchString = ''; // Store the original matched substring

    // Iterate backwards through matches to find the last valid one
    for (let i = extractedMatches.length - 1; i >= 0; i--) {
        let currentMatch = extractedMatches[i].trim(); 
        cnum = ''; 
        year = ''; 
        successfulParse = false; 
        parsedMatchString = currentMatch; 

        const separatorMatch = currentMatch.match(/^(.*?)(\s*[_\-/]\s*)(.*?)$/);
        if (separatorMatch && separatorMatch.length === 4) {
            const potentialNumPart = separatorMatch[1];
            const potentialYearPart = separatorMatch[3];
            
            cnum = (potentialNumPart.match(/\d{1,5}[a-z]{0,2}$/i) || [''])[0];
            year = (potentialYearPart.match(/^\d+$/) || [''])[0];
            if (cnum && year) {
                successfulParse = true;
                // console.log(`Parsed via separator: ${cnum} | ${year} from "${currentMatch}"`);
            }
        }

        if (!successfulParse) {
             
             const spaceParts = currentMatch.match(/^(\d{1,5}[a-z]{0,2})\s+((?:19|20)?\d{2})$/i);
             if (spaceParts && spaceParts.length === 3) {
                 cnum = spaceParts[1];
                 year = spaceParts[2];
                 successfulParse = true;
                 // console.log(`Parsed via space: ${cnum} | ${year} from "${currentMatch}"`);
             }
        }

        if (successfulParse) {
            break; 
        }
    } 


    // 5. Handle Parsing Failure (if no match could be parsed)
    if (!successfulParse || !cnum || !year) {
        // console.warn("Std: Cannot parse cnum/year from any extracted match:", extractedMatches);
        return processedInput.replace(/\s+/g, '-'); // Fallback
    }

    // 6. Format Year (2-digit to 4-digit)
    let originalYearLength = year.length;
    if (year.length === 2) {
        const yearInt = parseInt(year, 10);
        if (!isNaN(yearInt)) {
            // Heuristic: Assume years 30-99 are 19xx, 00-29 are 20xx
            if (yearInt >= 30 && yearInt <= 99) year = "19" + year;
            else if (yearInt >= 0 && yearInt <= 29) year = "20" + year;
            // else: keep the 2-digit year if it's outside expected range (e.g., '45')
        }
    }

    // 7. Year Plausibility Check (Only if it became 4 digits)
    if (year.length === 4) {
        const yearInt4 = parseInt(year, 10);
        if (isNaN(yearInt4) || yearInt4 < 1880 || yearInt4 > 2099) {
            // console.warn("Std: Unlikely 4-digit year parsed:", year);
            return processedInput.replace(/\s+/g, '-'); // Fallback
        }
    } else if (originalYearLength !== 2) {
        // console.warn("Std: Unexpected year format after processing:", year);
        return processedInput.replace(/\s+/g, '-'); // Fallback
    }

    const casenumSlashYearFinal = `${cnum}/${year}`;

    // 8. Apply Court-Specific Formatting
    if (court === 'sc') {
        // Apply SC Prefixing (using 'processedInput' for keyword checks)
        if (processedInput.includes("fr") || processedInput.includes("f/r")) {
            return "sc-fr-application-no-" + casenumSlashYearFinal;
        } else if (/\b(special|spl|spl la)\b/i.test(processedInput)) {
            return "sc-spl-la-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("appeal")) {
            return "sc-appeal-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("la")) {
            // Place 'la' check after 'spl la' to avoid incorrect matching
            return "sc-la-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("chc")) {
            // Typically CHC appeals might go to SC Appeal, adjust if needed
            return "sc-appeal-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("ca") && processedInput.includes("writ")) {
            return "sc-ca-writ-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("application")) {
            return "sc-application-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("contempt")) {
            return "sc-contempt-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("rule")) {
            return "sc-rule-no-" + casenumSlashYearFinal;
        } else if (processedInput.includes("revision")) {
            return "sc-revision-no-" + casenumSlashYearFinal;
        } else {
            // Fallback generic SC prefix if keywords don't match
            return "sc-" + casenumSlashYearFinal;
        }
    } else {
        
        // --- Non-SC Formatting  ---

        let nonSCStandardized = processedInput.replace(/\s+/g, '-');

        try {
            // Create the hyphenated version of the string that was successfully parsed
            const hyphenatedParsedString = parsedMatchString.trim().replace(/\s+/g, '-');
            // Escape this hyphenated string for use in regex
            const escapedHyphenatedParsed = hyphenatedParsedString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

            if (nonSCStandardized.includes(escapedHyphenatedParsed)) {
                 // Simple string replacement might be safer than complex regex here
                 nonSCStandardized = nonSCStandardized.replace(hyphenatedParsedString, casenumSlashYearFinal);
                 // console.log(`Non-SC Replaced: ${hyphenatedParsedString} with ${casenumSlashYearFinal}`);
            } else {
                 const escapedParsedMatch = parsedMatchString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

                 const matchRegex = new RegExp(escapedParsedMatch.replace(/\s+/g, '[- ]+')); // Allow hyphens or spaces between parts of the original match
                 if (matchRegex.test(nonSCStandardized)) {
                     nonSCStandardized = nonSCStandardized.replace(matchRegex, casenumSlashYearFinal);
                     // console.log(`Non-SC Replaced via Regex: ${matchRegex} with ${casenumSlashYearFinal}`);
                 } else {
                    // console.warn(`Std (Non-SC): Could not replace '${parsedMatchString}' (or its hyphenated form) in '${nonSCStandardized}'`);
                 }
            }

        } catch (e) {
            console.error("Std (Non-SC): Regex error during replacement:", e);
        }
         nonSCStandardized = nonSCStandardized.replace(new RegExp(`-${year}$`), `/${year}`);


        return nonSCStandardized;
    }
};